import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { useSystem } from '../context/SystemContext';

const SystemMonitoring = () => {
  const { monitoring, loadMonitoringData } = useSystem();
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    loadMonitoringData();
    const interval = setInterval(() => {
      loadMonitoringData();
      setLastUpdate(new Date());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [loadMonitoringData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'error';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatPercentage = (value) => {
    return `${Math.round(value)}%`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Sistem Monitörü
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Son güncelleme: {lastUpdate.toLocaleTimeString()}
          </Typography>
          <Tooltip title="Yenile">
            <IconButton onClick={() => loadMonitoringData()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* System Health Overview */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Sistem Sağlığı
            </Typography>
            <Grid container spacing={2}>
              {monitoring.services?.map((service) => (
                <Grid item xs={12} sm={6} md={4} key={service.name}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {getStatusIcon(service.status)}
                        <Typography variant="subtitle1">
                          {service.name}
                        </Typography>
                      </Box>
                      <Chip 
                        label={service.status} 
                        color={getStatusColor(service.status)}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {service.url}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Performans Metrikleri
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">CPU Kullanımı</Typography>
                <Typography variant="body2">
                  {formatPercentage(monitoring.performance?.cpu || 0)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={monitoring.performance?.cpu || 0}
                color={monitoring.performance?.cpu > 80 ? 'error' : monitoring.performance?.cpu > 60 ? 'warning' : 'primary'}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Bellek Kullanımı</Typography>
                <Typography variant="body2">
                  {formatPercentage(monitoring.performance?.memory || 0)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={monitoring.performance?.memory || 0}
                color={monitoring.performance?.memory > 80 ? 'error' : monitoring.performance?.memory > 60 ? 'warning' : 'primary'}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Disk Kullanımı</Typography>
                <Typography variant="body2">
                  {formatPercentage(monitoring.performance?.disk || 0)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={monitoring.performance?.disk || 0}
                color={monitoring.performance?.disk > 80 ? 'error' : monitoring.performance?.disk > 60 ? 'warning' : 'primary'}
              />
            </Box>
          </Paper>
        </Grid>

        {/* System Logs */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Son Sistem Logları
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Zaman</TableCell>
                    <TableCell>Seviye</TableCell>
                    <TableCell>Mesaj</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monitoring.logs?.slice(0, 10).map((log, index) => (
                    <TableRow key={index}>
                      <TableCell>{new Date(log.timestamp).toLocaleTimeString()}</TableCell>
                      <TableCell>
                        <Chip 
                          label={log.level} 
                          color={log.level === 'ERROR' ? 'error' : log.level === 'WARN' ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Active Tenants */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Aktif Kiracılar
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Kiracı Adı</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Kullanıcı Sayısı</TableCell>
                    <TableCell>Son Aktivite</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monitoring.activeTenants?.map((tenant) => (
                    <TableRow key={tenant.id}>
                      <TableCell>{tenant.name}</TableCell>
                      <TableCell>{tenant.domain}</TableCell>
                      <TableCell>
                        <Chip 
                          label={tenant.status} 
                          color={tenant.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{tenant.userCount}</TableCell>
                      <TableCell>{new Date(tenant.lastActivity).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemMonitoring;
